import { useEffect, useMemo, useRef, useState } from 'react';
import { Appointment } from '@weave/schema-gen-ts/dist/schemas/schedule/calendar-events/v1/calendar_events.pb';
import {
  EventLocationType,
  EventStatus,
  EventType,
} from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import dayjs from 'dayjs';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { formatDate } from '@frontend/date';
import { getFullName } from '@frontend/name';
import { formatDateTime, isValidDate, useCalendarEventsConfigurationShallowStore } from '@frontend/schedule-helpers';
import { DATETIME_FORMATS, DEFAULT_APPOINTMENT_STATUS } from '../../../../constants';
import { useOnboardingFlowInfo } from '../../../../hooks/onboarding-flow-hooks/use-onboarding-flow-info';
import {
  useGetAppointmentTypesV3,
  useGetProvidersV3,
} from '../../../../hooks/scheduler-v3/use-get-calendar-v3-queries-data';
import { useSchedulingLocationInfo } from '../../../../hooks/use-scheduling-location-info';
import { useCalendarLocationIds } from '../../../../stores/calendar-location.store';
import { ExtendedProvider } from '../../../../types';
import { transformDateSlotsToOpeningBlocks, transformV3ResponsesForReviewRequest } from '../../../../utils';
import { getDateByTimezone, isSameOrAfterDate, isTodayDate } from '../../../../utils/date-helpers';
import { dispatchFocusAppointmentEvent } from '../../../CalendarV3/use-calendar-custom-events';
import { normalizeString } from '../../utils';
import { AppointmentTypeExtended, NewAppointmentForm } from './NewAppointmentForm';
import { AppointmentAvailabilityFormValues, OnCreateCalendarEvent, OnUpdateCalendarEvent } from './types';

type Props = {
  closeModal: () => void;
  onAppointmentSuccess?: (appointment?: Appointment) => void;
};

const getStartEndDatesForAppointment = (startDate: string, duration: number) => {
  const startDateObj = dayjs(startDate);
  const endDateObj = startDateObj.add(duration, 'minute');

  return {
    startDate: startDateObj.format('YYYY-MM-DD'),
    startTime: startDateObj.format('HH:mm:ss'),
    endDate: endDateObj.format('YYYY-MM-DD'),
    endTime: endDateObj.format('HH:mm:ss'),
  };
};

const getBookableAppointmentTimes = ({
  providerSlots,
  isTodaySelected,
  locationTimezone,
}: {
  providerSlots?: {
    slots?: {
      startDateTime?: string;
      endDateTime?: string;
    }[];
  }[];
  isTodaySelected: boolean;
  locationTimezone: string;
}) => {
  try {
    const currentDateTimeInTimezone = isTodaySelected ? dayjs().tz(locationTimezone) : null;
    const bookableSlots = (providerSlots || []).flatMap((providerSlot) => providerSlot.slots || []);

    return bookableSlots.reduce<{ startTime: string; endTime: string }[]>((acc, slot) => {
      if (!slot.startDateTime || !slot.endDateTime) {
        return acc;
      }

      if (currentDateTimeInTimezone) {
        const slotDateTime = dayjs(slot.startDateTime).tz(locationTimezone);

        if (!slotDateTime.isValid() || slotDateTime.isBefore(currentDateTimeInTimezone)) {
          return acc;
        }
      }

      acc.push({
        startTime: formatDate(slot.startDateTime, DATETIME_FORMATS.ISO_DATETIME),
        endTime: formatDate(slot.endDateTime, DATETIME_FORMATS.ISO_DATETIME),
      });

      return acc;
    }, []);
  } catch (error) {
    console.error('Failed to process bookable appointment slots', error);
    return [];
  }
};

export const NewAppointmentFormContainerV3 = ({ closeModal, onAppointmentSuccess }: Props) => {
  const { calendarPageLocationIds } = useCalendarLocationIds();
  const { getLocationTimeZone, getScopeName } = useSchedulingLocationInfo();
  const { setShowOnNextLogin, setOnboardingFlowInfo, isBookAppointmentVisible } = useOnboardingFlowInfo();
  const isApptCreateSuccessRef = useRef(false);

  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const locationTimezone = getLocationTimeZone(selectedLocationId);

  const { isScheduleDefinitionsEnabled } = SchedulerV3.Helpers.useGetScheduleDefinitionsFeatureFlagDetails();

  const [selectedFormValuesForAvailabilityApi, setSelectedFormValuesForAvailabilityApi] =
    useState<AppointmentAvailabilityFormValues>({} as AppointmentAvailabilityFormValues);

  const { configuredCalendarEvent } = useCalendarEventsConfigurationShallowStore('configuredCalendarEvent');

  const { data: serviceTypesData, refetch: refetchServiceTypes } = SchedulerV3.Helpers.useGetServiceTypesV3Data({
    selectedLocationId,
    isEnabled: isScheduleDefinitionsEnabled,
  });

  const { data: appointmentTypesV3, refetch: refetchAppointmentTypes } = useGetAppointmentTypesV3(
    calendarPageLocationIds,
    !isScheduleDefinitionsEnabled
  );

  const { data: providersV3 } = useGetProvidersV3(calendarPageLocationIds);

  const { isIntegratedOffice } = DataSourcesQueries.useGetAccessibleIntegrations();

  useEffect(() => {
    return () => {
      if (!isApptCreateSuccessRef.current && isBookAppointmentVisible) {
        // set show onboarding flow info on next login if appointment is not created successfully
        setShowOnNextLogin();
      }
    };
  }, []);

  const appointmentTypesData = useMemo(() => {
    if (isScheduleDefinitionsEnabled) {
      return (serviceTypesData?.appointmentTypeGroups || []).flatMap((appointmentTypeGroup) => {
        const appointmentType = appointmentTypeGroup?.appointmentType;
        return (appointmentTypeGroup?.bookableAppointmentTypes || []).map((service) => {
          return {
            id: service.appointmentTypeId,
            durationMinutes: service.duration || 0,
            name: service.displayName || '',
            sourceId: appointmentType?.sourceId || '',
            externalId: appointmentType?.externalId || '',
            amount: service.prepaymentAmount || 0,
            locationId: service.locationId,
            active: appointmentType?.details?.act,
            appointmentTypeLabelColor: appointmentType?.details?.clr || '',
          };
        });
      });
    }

    return transformV3ResponsesForReviewRequest({
      // Filtered appointment types should have all unarchived appointment types and the selected appointment (in case of edit) even if if is an archived appointment
      // `active` property does not exist in v2, so it won't affect any of the v2 appointment types
      appointmentTypesDataV3: appointmentTypesV3?.appointmentTypes.filter((apptType) => {
        const isSelectedApptType =
          normalizeString(apptType.displayName || apptType.externalName || '') ===
          normalizeString(configuredCalendarEvent?.appointmentType);

        return isSelectedApptType || apptType.details?.act;
      }),
    }).transformedAppointmentTypes as AppointmentTypeExtended[];
  }, [
    isScheduleDefinitionsEnabled,
    serviceTypesData?.appointmentTypeGroups,
    appointmentTypesV3?.appointmentTypes,
    configuredCalendarEvent?.appointmentType,
  ]);

  const filteredProviderList = useMemo(() => {
    return (
      transformV3ResponsesForReviewRequest({
        practitionerDataV3: providersV3?.providers,
      }).transformedPractitioners as ExtendedProvider[]
    )
      .sort((a, b) => {
        const aFullName = getFullName(a).toLowerCase();
        const bFullName = getFullName(b).toLowerCase();
        return aFullName.localeCompare(bFullName);
      })
      .filter(
        // Filtered providers should have all active providers and the selected provider (in case of edit)
        // even if it is an archived provider, but not the hidden (Show on Calendar = false)
        // `isActive` property exists in v2 as well, but it won't affect list as we only get active providers from API in v2
        (provider) => provider.id === configuredCalendarEvent?.providerId || (!provider.hidden && provider.isActive)
      );
  }, [configuredCalendarEvent?.providerId, providersV3?.providers]);

  const createCalendarEventMutation = SchedulerV3.Mutations.useCreateCalendarEventMutation();
  const updateCalendarEventMutation = SchedulerV3.Mutations.useUpdateCalendarEventMutation();

  const availabilityApiParams = useMemo(() => {
    if (selectedFormValuesForAvailabilityApi.appointmentDate) {
      const selectedDate = getDateByTimezone(
        selectedFormValuesForAvailabilityApi.appointmentDate,
        DATETIME_FORMATS.US_DATE,
        locationTimezone
      );
      const startDateTime = selectedDate.startOf('day').format(DATETIME_FORMATS.ISO_DATETIME_WITH_OFFSET);
      const endDateTime = selectedDate.endOf('day').format(DATETIME_FORMATS.ISO_DATETIME_WITH_OFFSET);

      return {
        startDate: startDateTime,
        endDate: endDateTime,
      };
    }
    return {
      startDate: '',
      endDate: '',
    };
  }, [locationTimezone, selectedFormValuesForAvailabilityApi.appointmentDate]);

  const isFutureDateSelected =
    isValidDate(dayjs(selectedFormValuesForAvailabilityApi.appointmentDate)) &&
    isSameOrAfterDate(dayjs(selectedFormValuesForAvailabilityApi.appointmentDate), dayjs().startOf('day'));

  const listCalendarAvailabilitiesQuery = SchedulerV3.Queries.useListCalendarAvailabilitiesQuery({
    request: {
      locationId: selectedLocationId,
      calendarId: selectedFormValuesForAvailabilityApi.selectedPractitionerCalendarId || '',
      startDateTime: availabilityApiParams.startDate || '',
      endDateTime: availabilityApiParams.endDate || '',
      durationMinutes: selectedFormValuesForAvailabilityApi.duration,
      clientTimezone: dayjs.tz.guess(),
      appointmentTypeId: selectedFormValuesForAvailabilityApi?.appointmentType ?? '',
    },
    options: {
      retry: 1,
      enabled:
        !isScheduleDefinitionsEnabled &&
        !!selectedLocationId &&
        !!selectedFormValuesForAvailabilityApi.selectedPractitionerCalendarId &&
        !!availabilityApiParams.startDate &&
        !!availabilityApiParams.endDate &&
        !!selectedFormValuesForAvailabilityApi.duration &&
        isFutureDateSelected,
    },
  });

  const listBookableSlotsQuery = SchedulerV3.Queries.useListBookableAppointmentSlotsQuery({
    request: {
      startDateTime: availabilityApiParams.startDate,
      endDateTime: availabilityApiParams.endDate,
      locationId: selectedLocationId,
      providerIds: [selectedFormValuesForAvailabilityApi.selectedPractitionerId],
      slotDuration: selectedFormValuesForAvailabilityApi.duration,
    },
    options: {
      enabled: Boolean(
        isScheduleDefinitionsEnabled &&
          selectedLocationId &&
          selectedFormValuesForAvailabilityApi.selectedPractitionerId &&
          availabilityApiParams.startDate &&
          availabilityApiParams.endDate &&
          isFutureDateSelected
      ),
    },
  });

  const availableAppointmentTimes = useMemo(() => {
    const isTodaySelected = isTodayDate(dayjs(selectedFormValuesForAvailabilityApi.appointmentDate));

    if (isScheduleDefinitionsEnabled) {
      return getBookableAppointmentTimes({
        providerSlots: listBookableSlotsQuery.data?.providerSlots,
        isTodaySelected,
        locationTimezone,
      });
    }

    if (!listCalendarAvailabilitiesQuery.data?.availableSlots?.length) return [];

    return transformDateSlotsToOpeningBlocks(
      listCalendarAvailabilitiesQuery.data.availableSlots,
      isTodaySelected,
      locationTimezone
    );
  }, [
    isScheduleDefinitionsEnabled,
    listBookableSlotsQuery.data?.providerSlots,
    listCalendarAvailabilitiesQuery?.data?.availableSlots,
    selectedFormValuesForAvailabilityApi.appointmentDate,
    locationTimezone,
  ]);

  const onUpdateAppointment = async (
    {
      personId,
      startDate: appointmentDateTime,
      practitionerId,
      duration,
      id,
      practitionerCalendarId,
      referenceId,
      practitionerName,
      appointmentTypeName,
      appointmentTypeId,
      locationId,
      backgroundColor,
    }: OnUpdateCalendarEvent,
    status?: EventStatus
  ) => {
    const { startDate, startTime, endDate, endTime } = getStartEndDatesForAppointment(appointmentDateTime, duration);

    const locationTimezone = getLocationTimeZone(locationId);

    const updatedCalendarEvent = await updateCalendarEventMutation.mutateAsync({
      attendeeId: personId,
      attendeeStatus: status || DEFAULT_APPOINTMENT_STATUS,
      eventType: EventType.APPOINTMENT,
      locationType: EventLocationType.OFFICE,
      organizerId: practitionerId,
      organizerCalendarId: practitionerCalendarId || '',
      startDate,
      startTime,
      endDate,
      endTime,
      startTimezone: locationTimezone,
      endTimezone: locationTimezone,
      eventId: id,
      referenceId: referenceId || '',
      referenceTypeId: appointmentTypeId || '',
      details: {
        dur: duration,
        orNa: practitionerName,
        rTyNa: appointmentTypeName,
        col: backgroundColor || '',
      },
      existingStartDate: formatDate(configuredCalendarEvent?.startDate, DATETIME_FORMATS.ISO_DATE) || '',
    });

    onAppointmentSuccess?.({
      clientLocationIds: updatedCalendarEvent?.event?.sourceTenantId
        ? [updatedCalendarEvent?.event?.sourceTenantId]
        : [],
      clientLocationId: updatedCalendarEvent?.event?.sourceTenantId || '',
      id: updatedCalendarEvent?.event?.id || '',
      locationId: updatedCalendarEvent?.event?.locationId || '',
    });
  };

  const onCreateAppointment = async (
    {
      personId,
      startDate: appointmentDateTime,
      practitionerId,
      duration,
      practitionerCalendarId,
      locationId,
      sourceId,
      appointmentTypeId,
      practitionerName,
      appointmentTypeName,
      backgroundColor,
    }: OnCreateCalendarEvent,
    status?: EventStatus
  ) => {
    const { startDate, startTime, endDate, endTime } = getStartEndDatesForAppointment(appointmentDateTime, duration);

    const locationTimezone = getLocationTimeZone(locationId);
    const appointmentResponse = await createCalendarEventMutation.mutateAsync({
      attendeeId: personId,
      attendeeStatus: status || DEFAULT_APPOINTMENT_STATUS,
      eventType: EventType.APPOINTMENT,
      locationType: EventLocationType.OFFICE,
      organizerId: practitionerId,
      organizerCalendarId: practitionerCalendarId || '',
      startDate,
      startTime,
      endDate,
      endTime,
      startTimezone: locationTimezone,
      endTimezone: locationTimezone,
      locationId,
      referenceTypeId: appointmentTypeId || '',
      details: {
        dur: duration,
        orNa: practitionerName,
        rTyNa: appointmentTypeName,
        col: backgroundColor || '',
      },
      sourceTenantId: sourceId || locationId, // Using sourceId or locationId as sourceTenantId because non integrated offices do not have sourceTenantId
    });

    SchedulerV3.trackPendoEvent('appointment_created', {
      entryPoint: 'calendar',
      createdByUserType: 'provider',
      appointmentId: appointmentResponse?.event?.id || '',
      appointmentProviderId: practitionerId,
      appointmentType: appointmentTypeName ?? '',
      appointmentDate: startDate,
      appointmentStartTime: startTime,
      appointmentEndTime: endTime,
      clientId: personId,
      locationId: locationId,
      locationName: getScopeName(locationId),
    });

    if (isBookAppointmentVisible) {
      isApptCreateSuccessRef.current = true;
      setOnboardingFlowInfo({ status: 'completed_appointment_creation' });
      dispatchFocusAppointmentEvent({
        appointmentId: appointmentResponse?.event?.id || '',
        calendarDate: formatDateTime(appointmentDateTime, {
          fromTimezone: 'UTC',
          toTimezone: locationTimezone,
          outputFormat: 'MM/DD/YYYY',
        }),
        locationId: locationId,
      });
    }

    onAppointmentSuccess?.({
      clientLocationIds: appointmentResponse?.event?.sourceTenantId ? [appointmentResponse?.event?.sourceTenantId] : [],
      clientLocationId: appointmentResponse?.event?.sourceTenantId || '',
      id: appointmentResponse?.event?.id || '',
      locationId: appointmentResponse?.event?.locationId || '',
    });
  };

  return (
    <NewAppointmentForm
      availableAppointmentTimes={availableAppointmentTimes ?? []}
      closeModal={closeModal}
      isAvailableAppointmentTimesLoading={
        isScheduleDefinitionsEnabled ? listBookableSlotsQuery.isLoading : listCalendarAvailabilitiesQuery.isLoading
      }
      isScheduleV3
      isStatusDropdownVisible={!isIntegratedOffice}
      onCreateAppointment={onCreateAppointment}
      onUpdateAppointment={onUpdateAppointment}
      setSelectedFormValuesForAvailabilityApi={setSelectedFormValuesForAvailabilityApi}
      providersList={filteredProviderList}
      setSelectedLocationId={setSelectedLocationId}
      selectedLocationId={selectedLocationId}
      selectedLocationIds={calendarPageLocationIds}
      appointmentTypes={appointmentTypesData ?? []}
      refetchAppointmentTypes={() => {
        isScheduleDefinitionsEnabled ? refetchServiceTypes() : refetchAppointmentTypes();
      }}
    />
  );
};
